import type { CSSProperties } from 'vue';
import { freeGiftBadgeImage } from '@/features/Page/data/themes/bfFreeGiftTheme';
import type { rgbaString } from '@/features/PageSection/types/models';
import type { UpsellConfiguration } from '../types/upsellTypes';

type BfFreeGiftUpsellOptions = {
  id: string;
  singleSku: string;
  valuePackSku?: string;
  bulkPackSku: string;
  headerText: string;
  descriptionText: string;
  termsAndConditionsText: string;
  quantityBadgeText: string;
  mediaStyle?: { mobile?: CSSProperties; desktop?: CSSProperties };
};

const lilacColour: rgbaString = 'rgba(216, 185, 255, 1)';

const buttonStyle: CSSProperties = { flexShrink: 0, textDecoration: 'underline' };

const baseMediaStyle: CSSProperties = {
  position: 'absolute',
  right: 0,
  bottom: 0,
  width: '40%',
  zIndex: 1,
};

const createBfFreeGiftUpsells = ({
  id,
  singleSku,
  valuePackSku,
  bulkPackSku,
  headerText,
  descriptionText,
  termsAndConditionsText,
  quantityBadgeText,
  mediaStyle,
}: BfFreeGiftUpsellOptions): UpsellConfiguration[] => {
  const singleReplacementUpsell: UpsellConfiguration = {
    id,
    upsellProduct: { product: bulkPackSku },
    preset: { colour: 'purple' },
    upsellData: {
      content: {
        pillText: 'Bundle and Save',
        mainBody: { headerText, descriptionText },
        buttonText: 'Add to Cart',
        termsAndConditionsText,
        quantityBadgeText,
        badgeImage: freeGiftBadgeImage,
      },
      style: {
        mainBody: {
          accentTextColor: lilacColour,
          valuedAtLabelColor: lilacColour,
          valuedAtLabelMarginBottom: '-0.2rem',
          valuedAtMarginLeft: '0.75rem',
          containerStyle: { gridTemplateColumns: 'minmax(min-content, 60%) 1fr' },
          headerFontSize: '1.75rem',
          headerLineHeight: '96%',
          contentStyle: { mobile: { paddingTop: '0.5rem' } },
          descriptionStyle: { paddingBottom: 0 },
        },
        button: {
          style: {
            mobile: { ...buttonStyle, width: 'min(11.56rem, 46.24vw)', fontSize: '0.84rem' },
            desktop: { ...buttonStyle, width: '14rem', fontSize: '1rem' },
          },
        },
        media: {
          mediaContainer: { position: 'static' },
          mediaStyle: {
            mobile: { ...baseMediaStyle, ...mediaStyle?.mobile },
            desktop: { ...baseMediaStyle, ...mediaStyle?.desktop },
          },
        },
      },
    },
    displayRules: {
      requiredProductsInCart: { anyOf: [{ product: singleSku }] },
      excludedProductsInCart: {
        anyOf: bfFreeGiftBulkPackSkus.map((product) => ({ product })),
      },
      location: { include: ['/cart'] },
    },
    actions: {
      add: [{ product: bulkPackSku }],
      remove: [{ product: singleSku, quantity: 1 }],
      chooseFreeGift: true,
    },
  };

  if (!valuePackSku) return [singleReplacementUpsell];

  return [
    singleReplacementUpsell,
    {
      ...singleReplacementUpsell,
      id: `${id}-value-pack`,
      displayRules: {
        ...singleReplacementUpsell.displayRules,
        requiredProductsInCart: { anyOf: [{ product: valuePackSku }] },
      },
      actions: {
        add: [{ product: bulkPackSku }],
        chooseFreeGift: true,
      },
    },
  ];
};

const bfFreeGiftUpsellOptions: BfFreeGiftUpsellOptions[] = [
  {
    id: 'cart-bf-free-gift-stain-mouthwash',
    singleSku: '10068-PO',
    valuePackSku: 'STAIN_MOUTHWASH_VALUE_PACK',
    bulkPackSku: '10068-PO8',
    headerText: 'Buy 3 get 5 FREE{{valuedAtPrice}}<br><accentText>for {{upsellPrice}}</accentText>',
    descriptionText:
      'Plus an additional <bold>FREE</bold> gift <accentText>in the<br>Value Supply Bundle^</accentText>',
    termsAndConditionsText:
      '*Value based on full price of 8 bottles purchased separately.<br>^T&C’s apply. 1 bundle per cart. Free gift only available with purchase of bundle.',
    quantityBadgeText: 'x8',
  },
  {
    id: 'cart-bf-free-gift-v34-strips',
    singleSku: '10060-VS',
    valuePackSku: 'V34_STRIPS_VALUE_PACK',
    bulkPackSku: '10060-VS5',
    headerText: 'Buy 2 get 3 FREE{{valuedAtPrice}}<br><accentText>for {{upsellPrice}}</accentText>',
    descriptionText:
      'Plus an additional <bold>FREE</bold> gift <accentText>in<br>the Value Supply Bundle^</accentText>',
    termsAndConditionsText:
      '*Value based on full price of 5 boxes purchased separately.<br>^T&C’s apply. 1 bundle per cart. Free gift only available with purchase of bundle.',
    quantityBadgeText: 'x5',
    mediaStyle: {
      mobile: { width: '38%' },
      desktop: { right: '0.5rem' },
    },
  },
  {
    id: 'cart-bf-free-gift-tooth-armour',
    singleSku: '10065-TA',
    valuePackSku: 'TOOTH_ARMOUR_VALUE_PACK',
    bulkPackSku: 'TOOTH_ARMOUR_BFGS',
    headerText: 'Buy 4 get 6 FREE{{valuedAtPrice}}<br><accentText>for {{upsellPrice}}</accentText>',
    descriptionText:
      'Plus an additional <bold>FREE</bold> gift <accentText>in<br>the Value Supply Bundle^</accentText>',
    termsAndConditionsText:
      '*Value based on full price of 10 bottles purchased separately.<br>^T&C’s apply. 1 bundle per cart. Free gift only available with purchase of bundle.',
    quantityBadgeText: 'x10',
  },
  {
    id: 'cart-bf-free-gift-tooth-wipes',
    singleSku: '10069-TW',
    valuePackSku: 'TOOTH_WIPES_VALUE_PACK',
    bulkPackSku: '10069-TW15',
    headerText: 'Buy 6 get 9 FREE{{valuedAtPrice}}<br><accentText>for {{upsellPrice}}</accentText>',
    descriptionText:
      'Plus an additional <bold>FREE</bold> gift <accentText>in<br>the Value Supply Bundle^</accentText>',
    termsAndConditionsText:
      '*Value based on full price of 15 packets purchased separately.<br>^T&C’s apply. 1 bundle per cart. Free gift only available with purchase of bundle.',
    quantityBadgeText: 'x15',
    mediaStyle: {
      mobile: { width: '31%', right: '0.31rem', bottom: '0.5rem' },
      desktop: { width: '33%', right: '0.31rem', bottom: '0.63rem' },
    },
  },
  {
    id: 'cart-bf-free-gift-tru-fit',
    singleSku: '50001-TF-WH',
    bulkPackSku: '50001-TF-WH5',
    headerText: 'Buy 2 get 3 FREE{{valuedAtPrice}}<br><accentText>for {{upsellPrice}}</accentText>',
    descriptionText:
      'Plus an additional <bold>FREE</bold> gift <accentText>in the<br>Value Supply Bundle^</accentText>',
    termsAndConditionsText:
      '*Value based on full price of 5 toothbrushes purchased separately.<br>^T&C’s apply. 1 bundle per cart. Free gift only available with purchase of bundle.',
    quantityBadgeText: 'x5',
  },
];

const bfFreeGiftBulkPackSkus = bfFreeGiftUpsellOptions.map(({ bulkPackSku }) => bulkPackSku);

export const bfFreeGiftUpsells: UpsellConfiguration[] =
  bfFreeGiftUpsellOptions.flatMap(createBfFreeGiftUpsells);

export const bfFreeGiftReplacedUpsellIds = [
  'cart-stain-mouthwash-value-pack',
  'cart-v34-strips-value-pack',
  'cart-tooth-armour-value-pack',
  'cart-tooth-armour-value-pack-uk',
  'cart-tooth-wipes-value-pack',
  'cart-stain-mouthwash-value-pack-tru-fit-toothbrush',
];
